/**
 * Copyright (c) 2019-2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Stefan Doerr <s.doerr@acellera.com>
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
import { CifCategory, CifField } from "molstar/lib/mol-io/reader/cif";
import { ComponentBuilder } from "molstar/lib/mol-model-formats/structure/common/component";
import { EntityBuilder } from "molstar/lib/mol-model-formats/structure/common/entity";
import { Column } from "molstar/lib/mol-data/db";
import { getMoleculeType } from "molstar/lib/mol-model/structure/model/types";
import { ArrayTrajectory, } from "molstar/lib/mol-model/structure";
import { Task } from "molstar/lib/mol-task";
import { MmcifFormat } from "molstar/lib/mol-model-formats/structure/mmcif";
import { createModels } from "molstar/lib/mol-model-formats/structure/basic/parser";
import { createBasic } from "molstar/lib/mol-model-formats/structure/basic/schema";
import { Coordinates, Time, } from "molstar/lib/mol-model/structure/coordinates";
import { Cell } from "molstar/lib/mol-math/geometry/spacegroup/cell";
import { Vec3 } from "molstar/lib/mol-math/linear-algebra";
import { PluginStateObject, PluginStateTransform, } from "molstar/lib/mol-plugin-state/objects";
import { ParamDefinition as PD } from "molstar/lib/mol-util/param-definition";
import { truncateString } from "./utils";
import { trajectoryFromSdf } from "molstar/lib/mol-model-formats/structure/sdf";
function parseCrystalInfo(id, mol) {
    const results = [];
    const cell = {
        entry_id: CifField.ofString(id),
        length_a: CifField.ofString(mol["crystalinfo"]["a"].toString()),
        length_b: CifField.ofString(mol["crystalinfo"]["b"].toString()),
        length_c: CifField.ofString(mol["crystalinfo"]["c"].toString()),
        angle_alpha: CifField.ofString(mol["crystalinfo"]["alpha"].toString()),
        angle_beta: CifField.ofString(mol["crystalinfo"]["beta"].toString()),
        angle_gamma: CifField.ofString(mol["crystalinfo"]["gamma"].toString()),
        Z_PDB: CifField.ofString(mol["crystalinfo"]["z"].toString()),
        pdbx_unique_axis: CifField.ofString("?"),
    };
    results.push(CifCategory.ofFields("cell", cell));
    if (mol.crystalinfo.sGroup) {
        const symmetry = {
            entry_id: CifField.ofString(id),
            "space_group_name_H-M": CifField.ofString(mol["crystalinfo"]["sGroup"].join(" ")),
            Int_Tables_number: CifField.ofString("?"),
            cell_setting: CifField.ofString("?"),
            space_group_name_Hall: CifField.ofString("?"),
        };
        results.push(CifCategory.ofFields("symmetry", symmetry));
    }
    return results;
}
function parseConect(mol, sites) {
    const id = [];
    const conn_type_id = [];
    const value_order = [];
    const ptnr1_label_asym_id = [];
    const ptnr1_auth_seq_id = [];
    const ptnr1_label_atom_id = [];
    const ptnr1_label_alt_id = [];
    const ptnr1_PDB_ins_code = [];
    const ptnr2_label_asym_id = [];
    const ptnr2_auth_seq_id = [];
    const ptnr2_label_atom_id = [];
    const ptnr2_label_alt_id = [];
    const ptnr2_PDB_ins_code = [];
    let k = 1;
    var bondtype_map = {
        "1": "sing",
        "2": "doub",
        "3": "trip",
        ar: "arom",
        am: "amid",
        du: "delo",
        un: "unsp",
        nc: "ncon",
    };
    const bonds = mol["bonds"];
    for (let i = 0; i < bonds.length; i++) {
        const idxA = bonds[i][0];
        const idxB = bonds[i][1];
        // if (idxA > idxB) continue;
        id.push(`covale${k}`);
        conn_type_id.push("covale");
        value_order.push(bondtype_map[mol["bondtype"][i]] || "sing");
        ptnr1_label_asym_id.push(sites.label_asym_id.str(idxA));
        ptnr1_auth_seq_id.push(sites.auth_seq_id.int(idxA));
        ptnr1_label_atom_id.push(sites.label_atom_id.str(idxA));
        ptnr1_label_alt_id.push(sites.label_alt_id.str(idxA));
        ptnr1_PDB_ins_code.push(sites.pdbx_PDB_ins_code.str(idxA));
        ptnr2_label_asym_id.push(sites.label_asym_id.str(idxB));
        ptnr2_auth_seq_id.push(sites.auth_seq_id.int(idxB));
        ptnr2_label_atom_id.push(sites.label_atom_id.str(idxB));
        ptnr2_label_alt_id.push(sites.label_alt_id.str(idxB));
        ptnr2_PDB_ins_code.push(sites.pdbx_PDB_ins_code.str(idxB));
        k += 1;
    }
    const struct_conn = {
        id: CifField.ofStrings(id),
        conn_type_id: CifField.ofStrings(conn_type_id),
        pdbx_value_order: CifField.ofStrings(value_order),
        ptnr1_label_asym_id: CifField.ofStrings(ptnr1_label_asym_id),
        ptnr1_auth_seq_id: CifField.ofNumbers(ptnr1_auth_seq_id),
        ptnr1_label_atom_id: CifField.ofStrings(ptnr1_label_atom_id),
        pdbx_ptnr1_label_alt_id: CifField.ofStrings(ptnr1_label_alt_id),
        pdbx_ptnr1_PDB_ins_code: CifField.ofStrings(ptnr1_PDB_ins_code),
        ptnr2_label_asym_id: CifField.ofStrings(ptnr2_label_asym_id),
        ptnr2_auth_seq_id: CifField.ofNumbers(ptnr2_auth_seq_id),
        ptnr2_label_atom_id: CifField.ofStrings(ptnr2_label_atom_id),
        pdbx_ptnr2_label_alt_id: CifField.ofStrings(ptnr2_label_alt_id),
        pdbx_ptnr2_PDB_ins_code: CifField.ofStrings(ptnr2_PDB_ins_code),
    };
    return CifCategory.ofFields("struct_conn", struct_conn);
}
function getAtomSite(mol, label_entity_id, terIndices, options) {
    const pdbx_PDB_model_num = CifField.ofStrings(new Array(mol["name"].length).fill("1"));
    const auth_asym_id = CifField.ofStrings(mol["chain"]);
    const auth_seq_id = CifField.ofNumbers(mol["resid"]);
    const pdbx_PDB_ins_code = CifField.ofStrings(mol["insertion"]);
    const auth_atom_id = CifField.ofStrings(mol["name"]);
    const auth_comp_id = CifField.ofStrings(mol["resname"]);
    //@ts-ignore
    const id = CifField.ofStrings(mol["serial"]); // TODO: Convert serial from number to string (including hex?)
    // --------------------------------^^^^^^
    // molstar expects strings but molkit provides numbers!
    let currModelNum = pdbx_PDB_model_num.str(0);
    let currAsymId = auth_asym_id.str(0);
    let currSeqId = auth_seq_id.int(0);
    let currInsCode = pdbx_PDB_ins_code.str(0);
    let currLabelAsymId = currAsymId;
    let currLabelSeqId = currSeqId;
    const asymIdCounts = new Map();
    const atomIdCounts = new Map();
    const labelAsymIds = [];
    const labelAtomIds = [];
    const labelSeqIds = [];
    // serial label_seq_id if there are ins codes
    let hasInsCode = false;
    for (let i = 0, il = id.rowCount; i < il; ++i) {
        if (pdbx_PDB_ins_code.str(i) !== "") {
            hasInsCode = true;
            break;
        }
    }
    // ensure unique asym ids per model and unique atom ids per seq id
    for (let i = 0, il = id.rowCount; i < il; ++i) {
        const modelNum = pdbx_PDB_model_num.str(i);
        const asymId = auth_asym_id.str(i);
        const seqId = auth_seq_id.int(i);
        const insCode = pdbx_PDB_ins_code.str(i);
        let atomId = auth_atom_id.str(i);
        if (modelNum !== currModelNum) {
            asymIdCounts.clear();
            atomIdCounts.clear();
            currModelNum = modelNum;
            currAsymId = asymId;
            currSeqId = seqId;
            currInsCode = insCode;
            currLabelAsymId = asymId;
            currLabelSeqId = seqId;
        }
        else if (currAsymId !== asymId) {
            atomIdCounts.clear();
            currAsymId = asymId;
            currSeqId = seqId;
            currInsCode = insCode;
            currLabelAsymId = asymId;
            currLabelSeqId = seqId;
        }
        else if (currSeqId !== seqId) {
            atomIdCounts.clear();
            if (currSeqId === currLabelSeqId) {
                currLabelSeqId = seqId;
            }
            else {
                currLabelSeqId += 1;
            }
            currSeqId = seqId;
            currInsCode = insCode;
        }
        else if (currInsCode !== insCode) {
            atomIdCounts.clear();
            currInsCode = insCode;
            currLabelSeqId += 1;
        }
        if (asymIdCounts.has(asymId)) {
            // only change the chains name if there are TER records
            // otherwise assume repeated chain name use is from interleaved chains
            // also don't change the chains name if there are assemblies
            // as those require the original chain name
            if (terIndices.has(i) && !options.hasAssemblies) {
                const asymIdCount = asymIdCounts[asymId] + 1;
                asymIdCounts.set(asymId, asymIdCount);
                currLabelAsymId = `${asymId}_${asymIdCount}`;
            }
        }
        else {
            asymIdCounts.set(asymId, 0);
        }
        labelAsymIds[i] = currLabelAsymId;
        if (atomIdCounts.has(atomId)) {
            const atomIdCount = atomIdCounts[atomId] + 1;
            atomIdCounts.set(atomId, atomIdCount);
            atomId = `${atomId}_${atomIdCount}`;
        }
        else {
            atomIdCounts.set(atomId, 0);
        }
        labelAtomIds[i] = atomId;
        if (hasInsCode) {
            labelSeqIds[i] = currLabelSeqId;
        }
    }
    const labelAsymId = Column.ofStringArray(labelAsymIds);
    const labelAtomId = Column.ofStringArray(labelAtomIds);
    const label_seq_id = hasInsCode
        ? CifField.ofColumn(Column.ofIntArray(labelSeqIds))
        : CifField.ofUndefined(mol["name"].length, Column.Schema.int);
    const coords = new Float32Array(mol["coords"]);
    const numAtoms = mol["numAtoms"];
    return {
        auth_asym_id,
        auth_atom_id,
        auth_comp_id,
        auth_seq_id,
        B_iso_or_equiv: CifField.ofNumbers(mol["beta"]),
        Cartn_x: CifField.ofNumbers(coords.slice(0 * numAtoms, 1 * numAtoms)),
        Cartn_y: CifField.ofNumbers(coords.slice(1 * numAtoms, 2 * numAtoms)),
        Cartn_z: CifField.ofNumbers(coords.slice(2 * numAtoms, 3 * numAtoms)),
        group_PDB: CifField.ofStrings(mol["record"]),
        id,
        label_alt_id: CifField.ofStrings(mol["altloc"]),
        label_asym_id: CifField.ofColumn(labelAsymId),
        label_atom_id: CifField.ofColumn(labelAtomId),
        label_comp_id: auth_comp_id,
        label_seq_id,
        label_entity_id: CifField.ofStrings(label_entity_id),
        occupancy: CifField.ofNumbers(mol["occupancy"]),
        type_symbol: CifField.ofStrings(mol["element"]),
        pdbx_PDB_ins_code: CifField.ofStrings(mol["insertion"]),
        pdbx_PDB_model_num,
        partial_charge: CifField.ofNumbers(mol["charge"]),
        pdbx_formal_charge: CifField.ofNumbers(mol["formalcharge"]),
    };
}
function moleculeToMmCif(mol, header) {
    return __awaiter(this, void 0, void 0, function* () {
        const entityBuilder = new EntityBuilder();
        const helperCategories = [];
        const heteroNames = [];
        let hasAssemblies = false;
        // TODO: Get TER indices on changes of segments
        const terIndices = new Set();
        const segid = mol["segid"];
        for (let i = 0, il = segid.length; i < il - 1; ++i) {
            if (segid[i] !== segid[i + 1]) {
                terIndices.add(i + 1);
            }
        }
        // build entity and chem_comp categories
        const seqIds = Column.ofIntArray(mol["resid"]);
        const atomIds = Column.ofStringArray(mol["name"]);
        const compIds = Column.ofStringArray(mol["resname"]);
        const asymIds = Column.ofStringArray(mol["chain"]);
        const componentBuilder = new ComponentBuilder(seqIds, atomIds);
        componentBuilder.setNames(heteroNames);
        entityBuilder.setNames(heteroNames);
        const label_entity_id = [];
        for (let i = 0, il = compIds.rowCount; i < il; ++i) {
            const compId = compIds.value(i);
            const moleculeType = getMoleculeType(componentBuilder.add(compId, i).type, compId);
            label_entity_id[i] = entityBuilder.getEntityId(compId, moleculeType, asymIds.value(i));
        }
        const atom_site = getAtomSite(mol, label_entity_id, terIndices, {
            hasAssemblies,
        });
        // if (!isPdbqt) delete atom_site.partial_charge;
        if (mol.crystalinfo && Object.keys(mol["crystalinfo"]).length > 0) {
            helperCategories.push(...parseCrystalInfo("?", mol));
        }
        if (mol["bonds"].length > 0) {
            helperCategories.push(parseConect(mol, atom_site));
        }
        const categories = {
            entity: CifCategory.ofTable("entity", entityBuilder.getEntityTable()),
            chem_comp: CifCategory.ofTable("chem_comp", componentBuilder.getChemCompTable()),
            atom_site: CifCategory.ofFields("atom_site", atom_site),
            // atom_site_anisotrop: CifCategory.ofFields('atom_site_anisotrop', getAnisotropic(anisotropic))
        };
        for (const c of helperCategories) {
            categories[c.name] = c;
        }
        return {
            header: header || "MoleculeKit",
            categoryNames: Object.keys(categories),
            categories,
        };
    });
}
function handleSdfAtoms(mol) {
    const numAtoms = mol["numAtoms"];
    const coords = new Float32Array(mol["coords"]);
    const coords_x = coords.slice(0 * numAtoms, 1 * numAtoms);
    const coords_y = coords.slice(1 * numAtoms, 2 * numAtoms);
    const coords_z = coords.slice(2 * numAtoms, 3 * numAtoms);
    return {
        count: mol["numAtoms"],
        x: Column.ofFloatArray(coords_x),
        y: Column.ofFloatArray(coords_y),
        z: Column.ofFloatArray(coords_z),
        type_symbol: Column.ofStringArray(mol["element"]),
        formal_charge: Column.ofIntArray(mol["formalcharge"]),
    };
}
function handleSdfBonds(mol) {
    var bondtype_map = {
        "1": 1,
        "2": 2,
        "3": 3,
        "4": 4,
    };
    const bonds = mol["bonds"];
    const order = [];
    const idxA = [];
    const idxB = [];
    for (let i = 0; i < bonds.length; i++) {
        order.push(bondtype_map[mol["bondtype"][i]] || 1);
        idxA.push(bonds[i][0] + 1);
        idxB.push(bonds[i][1] + 1);
    }
    return {
        count: bonds.length,
        atomIdxA: Column.ofIntArray(idxA),
        atomIdxB: Column.ofIntArray(idxB),
        order: Column.ofIntArray(order),
    };
}
function moleculeToSdf(mol, header, i) {
    return __awaiter(this, void 0, void 0, function* () {
        let sdf;
        try {
            const title = `${header} (${i + 1})`; //`Molecule ${mIdx + 1}`;
            const program = "";
            const comment = "";
            const atoms = handleSdfAtoms(mol);
            const bonds = handleSdfBonds(mol);
            const numAtoms = mol["numAtoms"];
            const formalCharges = {
                atomIdx: Column.ofConst(0, numAtoms, Column.Schema.int),
                charge: Column.ofConst(0, numAtoms, Column.Schema.int),
            };
            const result = {
                title,
                program,
                comment,
                atoms,
                bonds,
                formalCharges,
            };
            sdf = {
                molFile: result,
                dataItems: {
                    dataHeader: Column.ofStringArray([]),
                    data: Column.ofStringArray([]),
                },
            };
        }
        catch (e) {
            console.error(e);
        }
        return sdf;
    });
}
function moleculesToSdf(mols, header) {
    var _a, mols_1, mols_1_1;
    var _b, e_1, _c, _d;
    return __awaiter(this, void 0, void 0, function* () {
        const compounds = [];
        let mIdx = 0;
        try {
            for (_a = true, mols_1 = __asyncValues(mols); mols_1_1 = yield mols_1.next(), _b = mols_1_1.done, !_b;) {
                _d = mols_1_1.value;
                _a = false;
                try {
                    let mol = _d;
                    const molFile = yield moleculeToSdf(mol, header, mIdx);
                    if (molFile)
                        compounds.push(molFile);
                    mIdx += 1;
                }
                finally {
                    _a = true;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (!_a && !_b && (_c = mols_1.return)) yield _c.call(mols_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return compounds;
    });
}
function coordinatesFromMoleculeKit(mol) {
    return Task.create("Parse MoleculeKit Coords", (ctx) => __awaiter(this, void 0, void 0, function* () {
        yield ctx.update("Converting to coordinates");
        const deltaTime = Time(mol.fstep ? mol.fstep * 1000 : 0, "ps");
        const offsetTime = Time(0, "ps");
        const coords = new Float32Array(mol["coords"]);
        const boxes = new Float32Array(mol["box"]);
        const numFrames = mol["numFrames"];
        const numAtoms = mol["numAtoms"];
        const frames = [];
        for (let i = 0, il = numFrames; i < il; ++i) {
            const x = Vec3.create(boxes[0 * numFrames + i], 0, 0);
            const y = Vec3.create(0, boxes[1 * numFrames + i], 0);
            const z = Vec3.create(0, 0, boxes[2 * numFrames + i]);
            const start_idx = i * 3 * numAtoms;
            frames.push({
                elementCount: mol["numAtoms"],
                cell: Cell.fromBasis(x, y, z),
                x: coords.slice(start_idx + 0 * numAtoms, start_idx + 1 * numAtoms),
                y: coords.slice(start_idx + 1 * numAtoms, start_idx + 2 * numAtoms),
                z: coords.slice(start_idx + 2 * numAtoms, start_idx + 3 * numAtoms),
                xyzOrdering: { isIdentity: true },
                time: Time(offsetTime.value + deltaTime.value * i, deltaTime.unit),
            });
        }
        return Coordinates.create(frames, deltaTime, offsetTime);
    }));
}
function coordinatesFromMoleculeKitSet(mols) {
    return Task.create("Parse MoleculeKit Coords", (ctx) => __awaiter(this, void 0, void 0, function* () {
        yield ctx.update("Converting to coordinates");
        const exmol = mols[0];
        const deltaTime = Time(exmol.fstep ? exmol.fstep * 1000 : 0, "ps");
        const offsetTime = Time(0, "ps");
        const numMols = mols.length;
        const frames = [];
        // let start_idx = 0;
        for (let m = 0, ml = numMols; m < ml; ++m) {
            const mol = mols[m];
            const box = new Float32Array(mol["box"]);
            const x = Vec3.create(box[0], 0, 0);
            const y = Vec3.create(0, box[1], 0);
            const z = Vec3.create(0, 0, box[2]);
            const numAtoms = mol["numAtoms"];
            const coords = new Float32Array(mol["coords"]);
            frames.push({
                elementCount: numAtoms,
                cell: Cell.fromBasis(x, y, z),
                x: coords.slice(0 * numAtoms, 1 * numAtoms),
                y: coords.slice(1 * numAtoms, 2 * numAtoms),
                z: coords.slice(2 * numAtoms, 3 * numAtoms),
                xyzOrdering: { isIdentity: true },
                time: Time(offsetTime.value + deltaTime.value * m, deltaTime.unit),
            });
            // start_idx += numAtoms * 3;
        }
        return Coordinates.create(frames, deltaTime, offsetTime);
    }));
}
function trajectoryProps(trajectory) {
    const first = trajectory.representative;
    return {
        label: `${first.entry}`,
        description: `${trajectory.frameCount} model${trajectory.frameCount === 1 ? "" : "s"}`,
    };
}
export const CoordinatesFromMoleculeKit = PluginStateTransform.BuiltIn({
    name: "coords-from-moleculekit",
    display: {
        name: "Parse MoleculeKit",
        description: "Parse MoleculeKit object and create trajectory.",
    },
    from: PluginStateObject.Root,
    to: PluginStateObject.Molecule.Coordinates,
    params: {
        mol: PD.Value({} /* cheatcode */, {
            isHidden: true,
        }),
    },
})({
    /* apply() is called when you call data.commit() */
    apply({ a /* this one gets the type in the "from" field above */, params }) {
        return Task.create("Parse MoleculeKit", (ctx) => __awaiter(this, void 0, void 0, function* () {
            const coordinates = yield coordinatesFromMoleculeKit(params.mol).runInContext(ctx);
            return new PluginStateObject.Molecule.Coordinates(coordinates, {
                label: a.label,
                description: "Coordinates",
            });
        }));
    },
});
export const CoordinatesFromMoleculeKitSet = PluginStateTransform.BuiltIn({
    name: "coords-from-moleculekit-set",
    display: {
        name: "Parse MoleculeKit set",
        description: "Parse MoleculeKit objects and create trajectory.",
    },
    from: PluginStateObject.Root,
    to: PluginStateObject.Molecule.Coordinates,
    params: {
        mols: PD.Value([] /* cheatcode */, {
            isHidden: true,
        }),
    },
})({
    /* apply() is called when you call data.commit() */
    apply({ a /* this one gets the type in the "from" field above */, params }) {
        return Task.create("Parse MoleculeKit", (ctx) => __awaiter(this, void 0, void 0, function* () {
            const coordinates = yield coordinatesFromMoleculeKitSet(params.mols).runInContext(ctx);
            return new PluginStateObject.Molecule.Coordinates(coordinates, {
                label: a.label,
                description: "Coordinates",
            });
        }));
    },
});
export const TrajectoryFromMoleculeKit = PluginStateTransform.BuiltIn({
    name: "trajectory-from-moleculekit",
    display: {
        name: "Parse MoleculeKit",
        description: "Parse MoleculeKit object and create trajectory.",
    },
    from: PluginStateObject.Root,
    to: PluginStateObject.Molecule.Trajectory,
    params: {
        mol: PD.Value({} /* cheatcode */, {
            isHidden: true,
        }),
        name: PD.Optional(PD.Text("")),
    },
})({
    /* apply() is called when you call data.commit() */
    apply({ a /* this one gets the type in the "from" field above */, params }) {
        return Task.create("Parse MoleculeKit", (ctx) => __awaiter(this, void 0, void 0, function* () {
            const cif = yield moleculeToMmCif(params.mol, params.name
                ? truncateString(params.name, 30)
                : "MoleculeKit" /*optional*/);
            const format = MmcifFormat.fromFrame(cif, undefined, {
                kind: "pdb",
                name: "MoleculeKit",
                data: null,
            });
            const basic = createBasic(format.data.db, true);
            const models = yield createModels(basic, format, ctx);
            const props = trajectoryProps(models);
            return new PluginStateObject.Molecule.Trajectory(models, props);
        }));
    },
});
export const TrajectoryFromMoleculeKitSet = PluginStateTransform.BuiltIn({
    name: "trajectory-from-moleculekit-set",
    display: {
        name: "Parse MoleculeKit set",
        description: "Parse MoleculeKit objects and create trajectory.",
    },
    from: PluginStateObject.Root,
    to: PluginStateObject.Molecule.Trajectory,
    params: {
        mols: PD.Value([] /* cheatcode */, {
            isHidden: true,
        }),
        name: PD.Optional(PD.Text("")),
    },
})({
    /* apply() is called when you call data.commit() */
    apply({ a /* this one gets the type in the "from" field above */, params }) {
        return Task.create("Parse MoleculeKit", (ctx) => __awaiter(this, void 0, void 0, function* () {
            const compounds = yield moleculesToSdf(params.mols, params.name
                ? truncateString(params.name, 30)
                : "MoleculeKit" /*optional*/);
            const models = [];
            for (const compound of compounds) {
                const traj = yield trajectoryFromSdf(compound).runInContext(ctx);
                for (let i = 0; i < traj.frameCount; i++) {
                    models.push(yield Task.resolveInContext(traj.getFrameAtIndex(i), ctx));
                }
            }
            const traj = new ArrayTrajectory(models);
            const props = trajectoryProps(traj);
            return new PluginStateObject.Molecule.Trajectory(traj, props);
        }));
    },
});
